import { useCallback } from 'react';
import { EAttributeType } from '../../../@types/AttributeTypes';
import { IBuildingProviderContext } from '../../../@types/ContextProviderRelated';
import { EWall } from '../../../@types/Enums';
import { EHistoryAction } from '../../../hooks/useUndoRedo';
import { IWall } from '../../../models/abstract/Wall';
import AttributeModel from '../../../models/standard/AttributeModel';
import PhysicalBoundaryModel from '../../../models/standard/PhysicalBoundary';
import RoomModel from '../../../models/standard/RoomModel';
import SpaceModel, { ISpace } from '../../../models/standard/SpaceModel';
import { useSelection } from '../../../provider/SelectionProvider';
import { DefaultAttributeId } from '../../../utils/attributes/defaultObjectsAndAttributes';
import { useAnnotations } from '../../AnnotationsProvider';

/**
 * Multi-room aware room actions.
 * Acts on the room that owns the currently-selected space (from SelectionProvider).
 */
export interface IRoomActions {
  setRoomTitle: (title: string) => void;
  updateRoomAttributes: (attributes: AttributeModel[]) => void;
  setRoomGeometryAttributes: (area: number, areaUnit: string | null, perimeter: number, perimeterUnit: string | null) => void;
  deleteRoom: () => void;
}

const wallsReferencedByRoom = (room: RoomModel): Set<string> => {
  const s = new Set<string>();
  (room.spaces ?? []).forEach((space: ISpace) => {
    // 1. Get standard walls from boundaries (outer shell)
    (space.boundaries ?? []).forEach((b) => {
      if (b instanceof PhysicalBoundaryModel) {
        (b.edges ?? []).forEach((e) => {
          if (e?.wall?.uuid) s.add(e.wall.uuid);
        });
        (b.walls ?? []).forEach((w) => {
          if (w?.uuid) s.add(w.uuid);
        });
      }
    });

    // 2. Explicitly grab partition walls from the space
    (space.walls ?? []).forEach((w) => {
      if (w?.uuid && w.type === EWall.PARTITION) {
        s.add(w.uuid);
      }
    });
  });
  return s;
};

const cleanupRemainingRoomsForRemovedWalls = (
  rooms: RoomModel[],
  removedWallUuids: Set<string>,
): RoomModel[] => {
  return rooms.map((room) => {
    const newRoom = new RoomModel(room.title ?? '', room.spaces.slice(), room.attributes ?? []);
    if ((room as any).uuid) newRoom.setUUID((room as any).uuid);

    newRoom.spaces = (room.spaces ?? []).map((space: ISpace) => {
      const keptSpaceWalls = (space.walls ?? []).filter((w) => !removedWallUuids.has(w?.uuid ?? ''));

      const newSpace = new SpaceModel(
        space.floor,
        space.products,
        space.elements,
        space.models2D,
        (space.boundaries ?? []).slice(),
        keptSpaceWalls,
      );
      if ((space as any).uuid && typeof (newSpace as any).setUUID === 'function') {
        (newSpace as any).setUUID((space as any).uuid);
      }

      newSpace.boundaries = (space.boundaries ?? []).map((b) => {
        if (!(b instanceof PhysicalBoundaryModel)) return b;
        const keptEdges = (b.edges ?? []).filter((e) => !removedWallUuids.has(e?.wall?.uuid ?? ''));
        const wallByUuid = new Map<string, IWall>();
        (b.walls ?? []).forEach((w) => {
          if (w?.uuid && !removedWallUuids.has(w.uuid)) wallByUuid.set(w.uuid, w);
        });
        keptEdges.forEach((edge) => {
          if (edge?.wall?.uuid) wallByUuid.set(edge.wall.uuid, edge.wall);
        });
        const keptWalls = Array.from(wallByUuid.values());
        return new PhysicalBoundaryModel(keptWalls, keptEdges);
      });

      return newSpace;
    });

    return newRoom;
  });
};

export const useRoomActions = (
  setBuilding: (building: React.SetStateAction<IBuildingProviderContext>, forceUpdate?: boolean) => void,
): IRoomActions => {
  const { selectedSpace } = useSelection();
  const { updateCameraPositionSprites } = useAnnotations();

  const findActiveRoomIndex = (rooms?: RoomModel[], spaceId?: string | null): number => {
    if (!rooms || rooms.length === 0) return -1;
    if (spaceId) {
      const i = rooms.findIndex(
        (r: any) =>
          Array.isArray(r?.spaces) &&
          r.spaces.some((s: any) => s?.uuid === spaceId),
      );
      if (i >= 0) return i;
    }
    return 0;
  };

  const setRoomTitle = useCallback(
    (title: string): void => {
      setBuilding((prev) => {
        const rooms = Array.isArray(prev.rooms) ? [...prev.rooms] : [];
        const rIdx = findActiveRoomIndex(
          rooms as any,
          selectedSpace?.spaceId,
        );
        if (rIdx < 0) return prev;

        const current = rooms[rIdx] as unknown as RoomModel;
        const attrs = current.attributes ?? [];
        const updatedAttrs =
          attrs.length === 0
            ? attrs
            : attrs.map((a) =>
                a.id === DefaultAttributeId.ROOM_NAME
                  ? new AttributeModel(
                      a.id,
                      a.key,
                      title,
                      a.predecessorId,
                      EAttributeType.TEXT,
                      a.unit,
                      a.hidden,
                      a.forced,
                      // Preserve extended properties
                      a.ownerId,
                      a.userId,
                      a.publicFlag,
                      a.localization,
                      a.defaults,
                      a.validation,
                      a.unitFamily
                    )
                  : a,
              );

        const nextRoom = new RoomModel(
          title,
          (current as any).spaces ?? [],
          updatedAttrs,
        );
        if ((current as any).uuid) nextRoom.setUUID((current as any).uuid);
        rooms[rIdx] = nextRoom;

        return {
          ...prev,
          rooms,
          isSetByUndoRedo: false,
          lastAction: EHistoryAction.ROOM_TITLE_CHANGED,
        };
      });
    },
    [setBuilding, selectedSpace?.spaceId],
  );

  const updateRoomAttributes = useCallback(
    (attributes: AttributeModel[]): void => {
      setBuilding((prev) => {
        const rooms = Array.isArray(prev.rooms) ? [...prev.rooms] : [];
        const rIdx = findActiveRoomIndex(
          rooms as any,
          selectedSpace?.spaceId,
        );
        if (rIdx < 0) return prev;

        const current = rooms[rIdx] as unknown as RoomModel;
        const nextRoom = new RoomModel(
          current.title,
          (current as any).spaces ?? [],
          attributes,
        );
        if ((current as any).uuid) nextRoom.setUUID((current as any).uuid);
        rooms[rIdx] = nextRoom;

        return {
          ...prev,
          rooms,
          isSetByUndoRedo: false,
          lastAction: EHistoryAction.ROOM_ATTRIBUES_UPDATED,
        };
      });
    },
    [setBuilding, selectedSpace?.spaceId],
  );

  const setRoomGeometryAttributes = useCallback(
    (
      area: number,
      areaUnit: string | null,
      perimeter: number,
      perimeterUnit: string | null,
    ) => {
      setBuilding((prev) => {
        const rooms = Array.isArray(prev.rooms) ? [...prev.rooms] : [];
        const rIdx = findActiveRoomIndex(
          rooms as any,
          selectedSpace?.spaceId,
        );
        if (rIdx < 0) return prev;

        const current = rooms[rIdx] as unknown as RoomModel;
        const attrs = current.attributes ?? [];
        if (attrs.length === 0) return prev;

        const updatedAttrs = attrs.map((a) => {
          if (a.id === DefaultAttributeId.ROOM_AREA)
            return new AttributeModel(
              a.id,
              a.key,
              area,
              a.predecessorId,
              EAttributeType.NUMBER,
              areaUnit,
              a.hidden,
              a.forced,
              // Preserve extended properties
              a.ownerId,
              a.userId,
              a.publicFlag,
              a.localization,
              a.defaults,
              a.validation,
              a.unitFamily
            );
          if (a.id === DefaultAttributeId.ROOM_PERIMETER)
            return new AttributeModel(
              a.id,
              a.key,
              perimeter,
              a.predecessorId,
              EAttributeType.NUMBER,
              perimeterUnit,
              a.hidden,
              a.forced,
              // Preserve extended properties
              a.ownerId,
              a.userId,
              a.publicFlag,
              a.localization,
              a.defaults,
              a.validation,
              a.unitFamily
            );
          return a;
        });

        const nextRoom = new RoomModel(
          current.title,
          (current as any).spaces ?? [],
          updatedAttrs,
        );
        if ((current as any).uuid) nextRoom.setUUID((current as any).uuid);
        rooms[rIdx] = nextRoom;

        return {
          ...prev,
          rooms,
          isSetByUndoRedo: false,
          lastAction:
            EHistoryAction.ROOM_GEOMETRY_ATTRIBUTES_UPDATED,
        };
      });
    },
    [setBuilding, selectedSpace?.spaceId],
  );

  const deleteRoom = useCallback((): void => {
    setBuilding((prev) => {
      const rooms = Array.isArray(prev.rooms) ? [...prev.rooms] : [];
      if (!rooms.length) return prev;

      const victimIdx = findActiveRoomIndex(
        rooms as any,
        selectedSpace?.spaceId,
      );
      if (victimIdx < 0) return prev;

      const victimRoom = rooms[victimIdx] as RoomModel;

      // Walls used by the victim room
      const victimWalls = wallsReferencedByRoom(victimRoom);

      // Remaining rooms
      const remainingRooms = rooms.filter(
        (_, i) => i !== victimIdx,
      ) as RoomModel[];

      // Walls still used by remaining rooms
      const stillUsedWalls = new Set<string>();
      remainingRooms.forEach((room) => {
        wallsReferencedByRoom(room).forEach((id) =>
          stillUsedWalls.add(id),
        );
      });

      // Walls referenced only by the victim
      const toRemove = new Set(
        Array.from(victimWalls).filter(
          (id) => !stillUsedWalls.has(id),
        ),
      );

      const allWalls = (prev.walls ?? []) as IWall[];
      const remainingWalls = allWalls.filter(
        (w) => !toRemove.has(w.uuid),
      );

      const cleanedRooms = cleanupRemainingRoomsForRemovedWalls(
        remainingRooms,
        toRemove,
      );
      updateCameraPositionSprites((prevSprites) => {
        const remainingSprites = prevSprites.filter((spr) => {
          const spaceUuid = spr.spaceUuid;
          if (!spaceUuid) return true;

          const stillExists = cleanedRooms.some((r) =>
            (r.spaces ?? []).some((s) => s.uuid === spaceUuid),
          );

          return stillExists;
        });

        return remainingSprites;
      });
      return {
        ...prev,
        walls: remainingWalls,
        rooms: cleanedRooms,
        isSetByUndoRedo: false,
        lastAction: EHistoryAction.ROOM_DELETED,
      };
    }, true);
  }, [setBuilding, selectedSpace?.spaceId, updateCameraPositionSprites]);

  return {
    setRoomTitle,
    updateRoomAttributes,
    setRoomGeometryAttributes,
    deleteRoom,
  };
};
